import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { Group } from "building-types/groups/group";
import type { DocEntityHandle, GroupHandle } from "building-types/handles/docEntityHandleTypes";
import type { Table } from "common/data/table";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import { docGetSystemEntityTable } from "../docSystemTable";
import { docEntityTypeToDocTableName } from "./docEntityTypeToDocTableName";
import { docGetEntityGroupId } from "./docGetEntityGroupId";
import { docGetGroup } from "./docGetGroup";

export interface CollapsedTableEntry<T extends DocEntityType = DocEntityType> {
  handle: GroupHandle | Extract<DocEntityHandle, { type: T }>;
  sortIndex: number;
  /** Top-level row object (ungrouped entity or group). */
  value: DocEntityBase<T> | Group;
}

export interface DocGetCollapsedTableEntriesOptions<T extends DocEntityType = DocEntityType> {
  /**
   * When true (default), grouped entities collapse into one top-level `entries`
   * row per group. When false, every matching entity is listed in `entries`
   * (no group rows). Independent of `includeEntityGroups`, which only controls
   * whether the `entityGroups` children map is built.
   */
  includeGroups?: boolean;
  /**
   * When true, also include entities from `docGetSystemEntityTable` (system
   * parameters, data sequences, system drawing templates, etc.).
   */
  includeSystemEntities?: boolean;
  /**
   * When true, populate `entityGroups` (group id → sorted members). Skip when
   * callers only need the collapsed top-level `entries` list. Does not change
   * which rows appear in `entries` — that is `includeGroups`.
   */
  includeEntityGroups?: boolean;
  /**
   * Filter applied to table entities before collapse. Groups appear only when
   * at least one matching member is present (unless listed in
   * `forceIncludeGroupIds`).
   */
  entityPredicate?: (entity: DocEntityBase<T>) => boolean;
  /**
   * Group ids to include even when they have no matching members (e.g. empty
   * content-mutable built-in Explorer folders).
   */
  forceIncludeGroupIds?: readonly string[];
}

export interface DocGetCollapsedTableEntriesResult<T extends DocEntityType = DocEntityType> {
  entries: CollapsedTableEntry<T>[];
  /**
   * Group id → member entities (sorted by sortIndex). Empty unless
   * `includeEntityGroups` is true. May include empty arrays for
   * `forceIncludeGroupIds`.
   */
  entityGroups: { [groupId: string]: DocEntityBase<T>[] };
}

/** Stable always-true predicate for callers that need a shared fallback reference. */
export const defaultEntityPredicate = (_entity: DocEntityBase) => true;

function entitySortIndex(entity: { sortIndex?: number }): number {
  return typeof entity.sortIndex === "number" ? entity.sortIndex : 0;
}

/**
 * Shared collapse+sort core for explorer-style table lists: ungrouped entities
 * of a type plus one entry per group, ordered by sortIndex.
 *
 * Group membership uses `docGetEntityGroupId` (includes SystemEntityGroups).
 */
export function docGetCollapsedTableEntries<T extends DocEntityType>(
  doc: Doc,
  entityType: T,
  {
    includeGroups = true,
    includeSystemEntities = false,
    includeEntityGroups = false,
    entityPredicate = defaultEntityPredicate,
    forceIncludeGroupIds,
  }: DocGetCollapsedTableEntriesOptions<T> = {},
): DocGetCollapsedTableEntriesResult<T> {
  const docTableName = docEntityTypeToDocTableName[entityType];

  // Some groupable types (e.g. SYSTEM_PARAMETER) only exist in system tables.
  if (!docTableName && !includeSystemEntities) {
    throw new Error(`Invalid entity type: ${entityType}`);
  }

  const collapseIntoGroups = includeGroups && entityType !== "GROUP";
  // Separate maps so entity ids and group ids never share a keyspace.
  const ungroupedEntries = new Map<string, CollapsedTableEntry<T>>();
  const groupEntries = new Map<string, CollapsedTableEntry<T>>();
  const entityGroupsAccum: { [groupId: string]: DocEntityBase<T>[] } | undefined = includeEntityGroups ? {} : undefined;

  const appendGroupMember = (groupId: string, entity: DocEntityBase<T>) => {
    if (!entityGroupsAccum) {
      return;
    }
    let members = entityGroupsAccum[groupId];
    if (!members) {
      members = [];
      entityGroupsAccum[groupId] = members;
    }
    members.push(entity);
  };

  const ensureGroupEntry = (groupId: string) => {
    if (groupEntries.has(groupId)) {
      return;
    }
    const groupHandle: GroupHandle = { type: "GROUP", id: groupId };
    const group = docGetGroup(doc, groupHandle);
    if (!group) {
      return;
    }
    groupEntries.set(groupId, {
      handle: groupHandle,
      sortIndex: entitySortIndex(group),
      value: group,
    });
  };

  const processTable = (table: Table<DocEntityBase<T>>) => {
    for (const id in table) {
      const entity = table[id];
      if (!entity || !entityPredicate(entity)) {
        continue;
      }

      const entityHandle = { type: entityType, id } as Extract<DocEntityHandle, { type: T }>;
      const groupId = collapseIntoGroups ? docGetEntityGroupId(doc, entityHandle) : undefined;
      if (groupId) {
        appendGroupMember(groupId, entity);
        ensureGroupEntry(groupId);
        continue;
      }

      ungroupedEntries.set(id, {
        handle: entityHandle,
        sortIndex: entitySortIndex(entity),
        value: entity,
      });
    }
  };

  if (docTableName) {
    processTable(doc[docTableName] as Table<DocEntityBase<T>>);
  }

  if (includeSystemEntities) {
    processTable(docGetSystemEntityTable(entityType));
  }

  if (collapseIntoGroups && forceIncludeGroupIds) {
    for (const groupId of forceIncludeGroupIds) {
      if (entityGroupsAccum && !entityGroupsAccum[groupId]) {
        entityGroupsAccum[groupId] = [];
      }
      ensureGroupEntry(groupId);
    }
  }

  const entityGroups: { [groupId: string]: DocEntityBase<T>[] } = {};
  if (entityGroupsAccum) {
    for (const groupId in entityGroupsAccum) {
      entityGroups[groupId] = sortEntitiesBySortIndex(entityGroupsAccum[groupId]);
    }
  }

  return {
    entries: sortEntitiesBySortIndex([...ungroupedEntries.values(), ...groupEntries.values()]),
    entityGroups,
  };
}
